import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { Animated, View } from 'react-native';
import { StateConfigContext } from '../../providers';
import { announceAccessibility, checkIsPlatform, getTestId } from '../../utils';
import { Notifier } from '../notifier';
import { Typography } from '../typography';
import { getToastAnimationStyles, getToastProps, getToastStyles } from './ToastStyles';
import { animateToast, hideToastHandler } from './ToastUtils';
import { useBreakpoint } from '../../hooks';
import { DynamicSvg } from '../dynamic-svg';
export var Toast = forwardRef((props, ref) => {
  var _a, _b, _c, _d;
  var stateContext = useContext(StateConfigContext);
  var toastProps = getToastProps(props, stateContext);
  var _useBreakpoint = useBreakpoint({
      returnAsObject: true
    }),
    isLargeScreen = _useBreakpoint.isLargeScreen;
  var toastStyles = getToastStyles(isLargeScreen, toastProps);
  var title = toastProps.title,
    show = toastProps.show,
    iconName = toastProps.iconName,
    showNotifier = toastProps.showNotifier,
    state = toastProps.state,
    dismissDuration = toastProps.dismissDuration,
    notifierSize = toastProps.notifierSize,
    notifierNumber = toastProps.notifierNumber,
    onDismissHandler = toastProps.onDismissHandler,
    config = toastProps.config,
    _toastProps$testID = toastProps.testID,
    testID = _toastProps$testID === void 0 ? 'Toast' : _toastProps$testID,
    style = toastProps.style;
  var fadeAnimRef = useRef(new Animated.Value(show ? 1 : 0));
  var toastTitleColor = (_b = (_a = config === null || config === void 0 ? void 0 : config.state) === null || _a === void 0 ? void 0 : _a[state]) === null || _b === void 0 ? void 0 : _b.titleColor;
  var toastIconProps = Object.assign({}, (_d = (_c = config === null || config === void 0 ? void 0 : config.state) === null || _c === void 0 ? void 0 : _c[state]) === null || _d === void 0 ? void 0 : _d.iconProps);
  var _useState = useState(show),
    showToast = _useState[0],
    setShowToast = _useState[1];
  var container = toastStyles.container,
    toastContainer = toastStyles.toastContainer,
    iconStyles = toastStyles.iconStyles,
    notifierStyles = toastStyles.notifierStyles,
    typographyStyles = toastStyles.typographyStyles;
  var timeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      (timeoutRef === null || timeoutRef === void 0 ? void 0 : timeoutRef.current) && clearTimeout(timeoutRef.current);
    };
  }, []);
  useEffect(() => {
    if (showToast) {
      animateToast(fadeAnimRef.current, true);
    }
  }, [showToast]);
  useEffect(() => {
    (timeoutRef === null || timeoutRef === void 0 ? void 0 : timeoutRef.current) && (title === null || title === void 0 ? void 0 : title.length) > 0 && openToast();
  }, [title]);
  var hideToastCallback = () => {
    animateToast(fadeAnimRef.current, false, () => {
      setShowToast(false);
      onDismissHandler === null || onDismissHandler === void 0 ? void 0 : onDismissHandler();
    });
  };
  var openToast = () => {
    setShowToast(true);
    hideToastHandler(timeoutRef, dismissDuration, hideToastCallback);
    var textsToAnnounce = [title];
    announceAccessibility(textsToAnnounce);
  };
  useEffect(() => {
    if (show) {
      openToast();
    } else {
      if (showToast) hideToastCallback();
    }
  }, [show]);
  return _jsx(_Fragment, {
    children: showToast && _jsxs(Animated.View, Object.assign({
      ref: ref,
      style: [container, getToastAnimationStyles(fadeAnimRef.current), style]
    }, getTestId(testID), checkIsPlatform('web') && {
      role: 'alert'
    }, {
      children: [_jsxs(View, Object.assign({
        style: toastContainer
      }, {
        children: [_jsx(DynamicSvg, Object.assign({}, getTestId(testID, 'icon'), toastIconProps, {
          iconName: iconName,
          style: [iconStyles, toastIconProps.style]
        })), _jsx(Typography, Object.assign({
          type: "body-para-sm-bold",
          text: title,
          color: toastTitleColor,
          style: typographyStyles
        }, getTestId(testID, 'title')))]
      })), !!showNotifier && _jsx(Notifier, Object.assign({
        style: notifierStyles,
        number: notifierNumber,
        size: notifierSize
      }, getTestId(testID, 'notifier')))]
    }))
  });
});